import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  CloudQueue as CloudIcon,
  Warning as WarningIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../features/auth/stores/authStore';
import { Routes as AppRoutes } from '../../../routes';

interface ConnectionErrorDetails {
  message: string;
  retryQueue: () => Promise<void>;
  isColdStart?: boolean; // True for ECONNREFUSED/503/504, false for other errors
}

/**
 * Dialog component for displaying connection errors to users.
 *
 * Listens to the global 'auth:network-error' event dispatched by the axios client
 * when token refresh fails due to network/infrastructure issues (e.g., cold starts).
 *
 * Features:
 * - Non-blocking modal dialog
 * - User can retry the failed request queue
 * - User can dismiss and continue (stays logged in)
 * - Loading state during retry
 * - Error feedback if retry fails
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <ConnectionErrorDialog />
 * ```
 */
export function ConnectionErrorDialog() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [isColdStart, setIsColdStart] = useState(false);
  const [retryQueue, setRetryQueue] = useState<(() => Promise<void>) | null>(
    null
  );

  useEffect(() => {
    const handleNetworkError = (event: Event) => {
      const customEvent = event as CustomEvent<ConnectionErrorDetails>;
      const { message, retryQueue, isColdStart = false } = customEvent.detail;

      setMessage(message);
      setRetryQueue(() => retryQueue); // Wrap in arrow function to avoid immediate invocation
      setIsColdStart(isColdStart);
      setOpen(true);
      setRetryError(null);
    };

    window.addEventListener('auth:network-error', handleNetworkError);

    return () => {
      window.removeEventListener('auth:network-error', handleNetworkError);
    };
  }, []);

  const handleRetry = async () => {
    if (!retryQueue) return;

    setRetrying(true);
    setRetryError(null);

    try {
      await retryQueue();
      // Success! Close dialog
      setOpen(false);
      setRetrying(false);
    } catch (error) {
      // Retry failed
      setRetrying(false);
      setRetryError(
        error instanceof Error
          ? error.message
          : 'Retry failed. Please try again.'
      );
    }
  };

  const handleDismiss = () => {
    setOpen(false);
    setRetrying(false);
    setRetryError(null);
  };

  const handleLogout = () => {
    console.log('👤 User initiated logout from ConnectionErrorDialog');
    logout('connection_issue_logout');
    setOpen(false);
    navigate(AppRoutes.login);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      aria-labelledby="connection-error-dialog-title"
      aria-describedby="connection-error-dialog-description"
    >
      <DialogTitle
        id="connection-error-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {isColdStart ? (
          <>
            <CloudIcon sx={{ color: 'success.main' }} />
            Starting Up...
          </>
        ) : (
          <>
            <WarningIcon color="warning" />
            Connection Issue
          </>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography
          id="connection-error-dialog-description"
          variant="body1"
          color="text.secondary"
          gutterBottom
          sx={{ whiteSpace: 'pre-line' }}
        >
          {message}
        </Typography>
        {retryError && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 2, p: 1.5, bgcolor: 'error.lighter', borderRadius: 1 }}
          >
            {retryError}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          You can retry the connection, dismiss this message and continue
          browsing, or sign out.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleLogout}
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
        >
          Sign Out
        </Button>
        <Button onClick={handleDismiss} variant="outlined" color="inherit">
          Dismiss
        </Button>
        <Button
          onClick={handleRetry}
          disabled={retrying}
          variant="contained"
          color="primary"
          startIcon={
            retrying ? <CircularProgress size={16} color="inherit" /> : null
          }
          autoFocus
        >
          {retrying ? 'Retrying...' : 'Retry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
